// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import { escapeHtml } from "../core/html.js";
import {
  aiSummaryAdvisoryMarkup,
  aiSummaryBriefItemsMarkup,
  aiSummaryDisclosureItemsMarkup,
  aiSummaryEvidenceRowsMarkup,
  aiSummaryEngineSectionsMarkup,
  aiSummaryExploratoryAnalysisMarkup,
  aiSummaryGeneratedLabel,
  aiSummaryGraphMarkup,
  aiSummaryIconMarkup,
  aiSummaryPayloadJson,
  aiSummaryQuickReadMarkup,
  aiSummaryReviewFocusMarkup,
  aiSummarySharedContextMarkup,
} from "./summary-markup.js";
import { flightBriefingMarkup } from "./flight-briefing-markup.js";

// Re-exported so app.ts wires briefing graph actions without growing its own
// import graph (the VM harnesses stub this module wholesale).
export { parseBriefingGraphTarget } from "./flight-briefing-graph-targets.js";

function aiSummaryPlanLimitInfo(aiSummaryState) {
  const info = aiSummaryState?.limitInfo;
  const limit = Math.max(0, Math.floor(Number(info?.limit ?? 0)));
  const current = Math.max(0, Math.floor(Number(info?.current ?? 0)));
  if (!limit || current < limit) return null;
  const plan = String(info?.plan ?? "").trim().toLowerCase();
  const planLabel = plan ? `${plan.slice(0, 1).toUpperCase()}${plan.slice(1)}` : "current";
  return {
    current,
    limit,
    planLabel,
    overLimit: current > limit,
    progress: Math.min(100, Math.round((current / limit) * 100)),
    packPurchasesEnabled: info?.packPurchasesEnabled === true,
    purchaseUrl: info?.purchaseUrl ?? "/#account/plan",
  };
}

function aiSummaryLimitNoticeMarkup(limitInfo) {
  if (!limitInfo) return "";
  const {
    current,
    limit,
    planLabel,
    overLimit,
    progress,
    packPurchasesEnabled,
    purchaseUrl,
  } = limitInfo;
  const usageMessage = overLimit
    ? `You've used ${current} AI requests this month. The ${planLabel} plan includes ${limit} monthly AI requests.`
    : `The ${planLabel} plan includes ${limit} monthly AI requests, and all ${current} have been used.`;
  const usageLabel = overLimit ? "used this month" : `of ${limit} used`;
  const meterValue = Math.min(current, limit);
  const meterText = overLimit
    ? `${current} AI requests used this month; ${limit} included with the ${planLabel} plan`
    : `${current} of ${limit} monthly AI requests used`;
  return `
    <section class="ai-summary-limit-notice" role="status" aria-labelledby="aiSummaryLimitTitle">
      <span class="ai-summary-limit-icon" aria-hidden="true">
        <svg viewBox="0 0 48 48" focusable="false">
          <path d="M12 32a12 12 0 0 1 24 0" />
          <path d="M24 19v12" />
          <circle cx="24" cy="33" r="2.5" />
        </svg>
      </span>
      <div class="ai-summary-limit-copy">
        <p class="ai-summary-limit-eyebrow">AI allowance</p>
        <h3 id="aiSummaryLimitTitle">AI summary limit reached</h3>
        <p>${escapeHtml(usageMessage)} Flight summaries, trend summaries, and Graph Ask share this allowance.</p>
        <span class="ai-summary-limit-renewal">Your next allowance begins at the start of next month.</span>
        ${packPurchasesEnabled ? `<a class="secondary-action ai-summary-limit-purchase" href="${escapeHtml(purchaseUrl)}">Buy more AI requests</a>` : ""}
      </div>
      <div class="ai-summary-limit-usage">
        <strong>${escapeHtml(current)} <span>${escapeHtml(usageLabel)}</span></strong>
        <div class="ai-summary-limit-meter" role="progressbar" aria-label="Monthly AI requests used" aria-valuemin="0" aria-valuemax="${escapeHtml(limit)}" aria-valuenow="${escapeHtml(meterValue)}" aria-valuetext="${escapeHtml(meterText)}">
          <span style="width: ${escapeHtml(progress)}%"></span>
        </div>
        <span class="ai-summary-limit-caption">Monthly requests</span>
      </div>
    </section>
  `;
}

export function aiSummaryPanelModel({
  aiSummaryState,
  aiSummaryEvidenceExpanded,
  aiSummaryAvailableForEntry,
  aiSummaryLoadingMarkup,
  available,
  hasCachedSummary,
  graphSpecs = {},
  readOnly = false,
  readOnlyActionMessage = () => "This action is disabled in demo mode.",
  summaryContext = null,
  availableSeriesKeys = null,
}) {
  const row = aiSummaryState.payload?.aiSummary ?? null;
  const summary = aiSummaryPayloadJson(aiSummaryState.payload, summaryContext);
  const generated = aiSummaryGeneratedLabel(row);
  const model = summary?.generator?.model ?? row?.model ?? "";
  const modelLabel = model.replace(/-\d{4}-\d{2}-\d{2}$/, "");
  const metadata = [generated ? `Generated ${generated}` : "", modelLabel ? `Model ${modelLabel}` : ""].filter(Boolean);
  const loading = aiSummaryState.status === "loading" || aiSummaryState.status === "preparing";
  const error = aiSummaryState.status === "error";
  const idle = aiSummaryState.status === "idle";
  const limitInfo = aiSummaryPlanLimitInfo(aiSummaryState);
  const quickReadItems = [
    summary?.overview,
    ...(Array.isArray(summary?.whatStandsOut) ? summary.whatStandsOut.slice(0, 2).map((item) => item?.text) : []),
  ].filter(Boolean);
  const hasEngineSummaries = Array.isArray(summary?.engineSummaries) && summary.engineSummaries.length > 1;

  return {
    aiSummaryEvidenceExpanded,
    aiSummaryAvailableForEntry,
    aiSummaryLoadingMarkup,
    aiSummaryState,
    available,
    error,
    graphSpecs,
    hasCachedSummary,
    hasEngineSummaries,
    idle,
    loading,
    limitInfo,
    metadata,
    quickReadItems,
    readOnly,
    readOnlyActionMessage,
    // Route by stored schema family (plan: no runtime coercion). V1 rows keep
    // rendering through the V1 sections below; V2 rows render the briefing.
    // No staleness state exists in this model (decision 14).
    isBriefing: summary?.schemaVersion === "flight-ai-summary/v2",
    availableSeriesKeys,
    summary,
  };
}

export function aiSummaryPanelMarkup(model) {
  const {
    aiSummaryEvidenceExpanded,
    aiSummaryState,
    available,
    error,
    graphSpecs,
    hasCachedSummary,
    hasEngineSummaries,
    idle,
    isBriefing,
    loading,
    limitInfo,
    metadata,
    quickReadItems,
    readOnly,
    readOnlyActionMessage,
    summary,
  } = model;
  const generationDisabled = readOnly || loading || !available || Boolean(limitInfo);
  const readOnlyTitle = readOnly ? ` title="${escapeHtml(readOnlyActionMessage("Generating AI summaries"))}" aria-disabled="true" data-demo-locked` : "";
  const generateActionAttributes = `${readOnlyTitle} ${generationDisabled ? "disabled" : ""}`.trim();
  const regenerateAction = summary ? `<div class="ai-summary-regenerate-action">
    <button class="secondary-action button-with-icon" type="button" data-ai-summary-regenerate ${generateActionAttributes}>
      ${aiSummaryIconMarkup("refresh")}
      <span>Regenerate</span>
    </button>
  </div>` : "";
  const briefingToolbar = isBriefing ? `
    <div class="fb-toolbar">
      <h2 class="fb-visually-hidden" id="aiSummaryTitle">AI Flight Summary</h2>
      <p class="fb-toolbar-meta">${escapeHtml(metadata.join(" · "))}</p>
      ${regenerateAction}
    </div>` : "";

  return `
    ${isBriefing ? "" : `<div class="ai-summary-topbar">
      <div class="ai-summary-heading">
        <h2 id="aiSummaryTitle">AI Flight Summary</h2>
        <p class="ai-summary-meta">${escapeHtml(metadata.join(" · "))}</p>
      </div>
      <div class="chart-actions">
        ${regenerateAction}
      </div>
    </div>`}
    <div class="ai-summary-body" id="aiSummaryBody">
      ${idle ? `
        <div class="ai-summary-empty-card">
          <div class="ai-summary-empty-content">
            <span class="ai-summary-empty-icon" aria-hidden="true">
              <svg viewBox="0 0 64 64" focusable="false">
                <path class="ai-summary-empty-icon-frame" d="M23 9H11a7 7 0 0 0-7 7v37a7 7 0 0 0 7 7h37a7 7 0 0 0 7-7V41" />
                <path class="ai-summary-empty-icon-sparkle" d="M47 4l4.5 11.5L63 20l-11.5 4.5L47 36l-4.5-11.5L31 20l11.5-4.5z" />
                <path class="ai-summary-empty-icon-sparkle is-small" d="M34 5l1.8 4.2L40 11l-4.2 1.8L34 17l-1.8-4.2L28 11l4.2-1.8z" />
                <path class="ai-summary-empty-icon-text" d="M18.5 45l7-20h5l7 20h-5.2l-1.3-4H25l-1.3 4zm7.8-8.2h5.4L29 28.5z" />
                <path class="ai-summary-empty-icon-text" d="M40 45V25h5v20z" />
              </svg>
            </span>
            <div>
              <strong>Ready to build your AI Flight Summary</strong>
              ${available ? "<p>A plain-language read of how the engine looked this flight, what to watch, and what to keep in mind.</p>" : "<p>Available once this flight is saved and analyzed.</p>"}
              ${hasCachedSummary ? "" : `<button class="primary-action" type="button" data-ai-summary-empty-action ${generateActionAttributes}>Generate Summary</button>`}
            </div>
          </div>
          <div class="ai-summary-empty-preview" aria-hidden="true">
            <div class="ai-summary-empty-preview-header">
              <span></span>
              <span></span>
            </div>
            <span class="ai-summary-empty-preview-line is-long"></span>
            <span class="ai-summary-empty-preview-line"></span>
            <span class="ai-summary-empty-preview-line is-short"></span>
            <div class="ai-summary-empty-preview-chips">
              <span>Findings</span>
              <span>Caveats</span>
              <span>Focus</span>
            </div>
          </div>
        </div>
      ` : ""}
      ${loading ? model.aiSummaryLoadingMarkup(aiSummaryState.status) : ""}
      ${error ? `
        ${aiSummaryLimitNoticeMarkup(limitInfo) || `<div class="ai-summary-error">
          <p>${escapeHtml(aiSummaryState.error)}</p>
          ${model.aiSummaryAvailableForEntry() ? `<button class="primary-action" type="button" data-ai-summary-empty-action ${generateActionAttributes}>Generate Summary</button>` : ""}
        </div>`}
      ` : ""}
      ${summary && !loading && !error && isBriefing ? flightBriefingMarkup(summary, {
        availableSeriesKeys: model.availableSeriesKeys ?? null,
        toolbarMarkup: briefingToolbar,
      }) : ""}
      ${summary && !loading && !error && !isBriefing ? `
        <div class="ai-summary-brief-grid">
          <section class="ai-summary-quick-read" aria-labelledby="aiSummaryQuickReadTitle">
            <div class="ai-summary-quick-read-header">
              <h3 id="aiSummaryQuickReadTitle">${aiSummaryIconMarkup("sparkle")} Quick Read</h3>
              ${aiSummaryAdvisoryMarkup()}
            </div>
            ${aiSummaryQuickReadMarkup(summary, quickReadItems, { includePhaseContext: !hasEngineSummaries })}
            ${aiSummaryGraphMarkup(graphSpecs.quickRead)}
          </section>
          <aside class="ai-summary-review-focus" aria-labelledby="aiSummaryReviewFocusTitle">
            <h3 id="aiSummaryReviewFocusTitle">What to Watch</h3>
            ${aiSummaryReviewFocusMarkup(summary.suggestedReviewFocus)}
          </aside>
        </div>
        ${aiSummaryEngineSectionsMarkup(summary.engineSummaries)}
        ${aiSummarySharedContextMarkup(summary)}
        ${aiSummaryExploratoryAnalysisMarkup(summary.exploratoryAnalysis)}
        <div class="ai-summary-detail-grid">
          <section class="ai-summary-section ai-summary-standouts" aria-labelledby="aiSummaryStandoutsTitle">
            <h3 id="aiSummaryStandoutsTitle">What Stands Out</h3>
            ${aiSummaryGraphMarkup(graphSpecs.standouts)}
            ${aiSummaryBriefItemsMarkup(summary.whatStandsOut)}
          </section>
          <section class="ai-summary-section ai-summary-evidence" aria-labelledby="aiSummaryEvidenceTitle">
            <h3 id="aiSummaryEvidenceTitle">The Details</h3>
            ${aiSummaryGraphMarkup(graphSpecs.evidence)}
            ${aiSummaryEvidenceRowsMarkup(summary.supportingEvidence, aiSummaryEvidenceExpanded)}
          </section>
        </div>
        <div class="ai-summary-disclosure-grid">
          <details class="ai-summary-disclosure">
            <summary>
              <span>${aiSummaryIconMarkup("caution")} Good to Know</span>
              <b>Show more</b>
            </summary>
            ${aiSummaryGraphMarkup(graphSpecs.caveats)}
            ${aiSummaryDisclosureItemsMarkup(summary.caveats, "Nothing extra to keep in mind for this flight.")}
          </details>
        </div>
      ` : ""}
    </div>
  `;
}
